import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditCard, Plus, X, Loader2, Trash2, Pencil, CalendarClock, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface Forma {
  id: number; forma: string; label: string; conta_id: number | null; conta_nome: string | null
  ativo: boolean; taxa_pct: number; dias_recebimento: number; a_prazo: boolean
  taxas_parcelas?: Record<string, number> | null
  gera_receber?: boolean
  // Código FEBRABAN que vai na NF-e. Vazio = o sistema deduz pelo código da forma.
  tpag?: string | null
}

// tPag da NT 2020.006 — o que a SEFAZ aceita no <detPag>.
const TPAG_OPCOES: [string, string][] = [
  ['01', '01 — Dinheiro'], ['02', '02 — Cheque'],
  ['03', '03 — Cartão de crédito'], ['04', '04 — Cartão de débito'],
  ['05', '05 — Crédito da loja'], ['10', '10 — Vale alimentação'],
  ['11', '11 — Vale refeição'], ['12', '12 — Vale presente'],
  ['13', '13 — Vale combustível'], ['15', '15 — Boleto bancário'],
  ['16', '16 — Depósito bancário'], ['17', '17 — PIX'],
  ['18', '18 — Transferência bancária'], ['19', '19 — Programa de fidelidade'],
  ['90', '90 — Sem pagamento'], ['99', '99 — Outros'],
]
interface Condicao { id: number; forma: string; nome: string; dias: number[]; parcelas: number; ativo: boolean; ordem: number }

// Cor por forma (mesma paleta do PDV) — faixa/pontinho no card. Inline p/ não sofrer purge do Tailwind.
const FORMA_COR: Record<string, string> = {
  dinheiro: '#10b981', pix: '#14b8a6', cartao_debito: '#3b82f6', cartao_credito: '#8b5cf6',
  link: '#6366f1', a_vista: '#16a34a', prazo: '#78716c', crediario: '#f59e0b', boleto: '#64748b',
  recebimento_bike: '#d946ef', credito_cliente: '#06b6d4', cheque: '#f97316',
  bonificacao: '#f43f5e', troca: '#ec4899',
}
const corDaForma = (forma: string) => FORMA_COR[forma] || '#94a3b8'
// FEATURE 03/07: operadora. tipo 'cartao' (máquina, sob Débito/Crédito) | 'link' (FixPay → forma 'link', com pix).
// taxas_credito = {"1":à vista, "2".."12"}.
interface Operadora { id: number; nome: string; tipo: string; ativo: boolean; ordem: number; taxa_debito: number; taxas_credito: Record<string, number>; taxa_pix: number }

export default function FormasPagamentoPage() {
  const qc = useQueryClient()
  const [novaForma, setNovaForma] = useState(false)
  const [editForma, setEditForma] = useState<Forma | null>(null)

  const { data: formas = [], isLoading } = useQuery<Forma[]>({
    queryKey: ['cfg-formas'],
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  const { data: contas = [] } = useQuery<any[]>({
    queryKey: ['contas-banco-formas'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/caixa/config-formas/${id}`)).data,
    onSuccess: () => { toast.success('Forma excluída'); qc.invalidateQueries({ queryKey: ['cfg-formas'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CreditCard className="text-brand-600" /> Formas de Pagamento</h1>
          <p className="text-ink-500 text-sm">Cadastre as formas, a conta que recebe, taxa, e os <strong>prazos</strong> (parcelas) de cada uma.</p>
        </div>
        <button onClick={() => setNovaForma(true)} className="btn-primary"><Plus size={14} /> Nova forma</button>
      </div>

      {isLoading && <div className="text-ink-400">Carregando...</div>}
      <div className="space-y-3">
        {formas.map((f) => (
          <div key={f.id} className="bg-white border border-ink-200 border-l-4 rounded-lg p-4"
            style={{ borderLeftColor: corDaForma(f.forma) }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: corDaForma(f.forma) }} />
                  {f.label}
                  <span className="text-[10px] font-mono text-ink-400">{f.forma}</span>
                  {!f.ativo && <span className="text-[10px] px-2 py-0.5 rounded bg-ink-200 text-ink-600">inativo</span>}
                  {f.a_prazo && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700">a prazo</span>}
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  Conta: {f.conta_nome || '—'} · Taxa: {f.taxa_pct}% · Receb.: {f.dias_recebimento}d
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditForma(f)} className="btn-secondary !py-1 !px-2 text-xs"><Pencil size={13} /></button>
                <button onClick={() => pedirExclusao({ descricao: `a forma "${f.label}"`, onConfirm: () => excluir.mutate(f.id) })}
                  className="btn-secondary !py-1 !px-2 text-xs text-rose-700"><Trash2 size={13} /></button>
              </div>
            </div>
            {f.a_prazo && <Prazos forma={f.forma} />}
          </div>
        ))}
      </div>

      {/* FEATURE 03/07: operadoras de cartão (FixPay etc.) — escolhidas no PDV sob Débito/Crédito. */}
      <OperadorasCartao />

      {(novaForma || editForma) && (
        <FormaModal forma={editForma} contas={contas}
          onClose={() => { setNovaForma(false); setEditForma(null) }}
          onDone={() => { setNovaForma(false); setEditForma(null); qc.invalidateQueries({ queryKey: ['cfg-formas'] }) }} />
      )}
    </div>
  )
}

// ============================================================
// OPERADORAS DE CARTÃO (FEATURE 03/07)
// Cada operadora (ex.: FixPay) tem taxa de débito e taxa de crédito por nº de parcelas.
// No PDV, ao escolher Débito/Crédito, o operador seleciona a operadora e a taxa fica
// gravada na venda. Fase 1: só registro (não lança no financeiro/DRE).
// ============================================================
function OperadorasCartao() {
  const qc = useQueryClient()
  const [nova, setNova] = useState(false)
  const [edit, setEdit] = useState<Operadora | null>(null)
  const { data: ops = [], isLoading } = useQuery<Operadora[]>({
    queryKey: ['operadoras-cartao'],
    queryFn: async () => (await api.get('/caixa/operadoras-cartao')).data,
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/caixa/operadoras-cartao/${id}`)).data,
    onSuccess: () => { toast.success('Operadora excluída'); qc.invalidateQueries({ queryKey: ['operadoras-cartao'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  return (
    <div className="pt-4 mt-2 border-t border-ink-200 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><CreditCard size={18} className="text-brand-600" /> Operadoras de cartão</h2>
          <p className="text-ink-500 text-sm">Máquinas/adquirentes (ex.: FixPay) com suas taxas. No PDV, ao escolher <strong>Débito</strong> ou <strong>Crédito</strong>, o operador seleciona a operadora.</p>
        </div>
        <button onClick={() => setNova(true)} className="btn-primary"><Plus size={14} /> Nova operadora</button>
      </div>
      {isLoading && <div className="text-ink-400 text-sm">Carregando...</div>}
      <div className="space-y-2">
        {ops.map((o) => {
          const cred = o.taxas_credito || {}
          const parcelas = Object.keys(cred).map(Number).filter(n => n >= 2).sort((a, b) => a - b)
          return (
            <div key={o.id} className="bg-white border border-ink-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {o.nome}
                    <span className={`text-[10px] px-2 py-0.5 rounded ${o.tipo === 'link' ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'}`}>
                      {o.tipo === 'link' ? 'Link' : 'Cartão'}
                    </span>
                    {!o.ativo && <span className="text-[10px] px-2 py-0.5 rounded bg-ink-200 text-ink-600">inativo</span>}
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5">
                    Débito: <strong>{o.taxa_debito}%</strong>
                    {cred['1'] != null && <> · Crédito à vista: <strong>{cred['1']}%</strong></>}
                    {parcelas.length > 0 && <> · {`${parcelas[0]}x a ${parcelas[parcelas.length - 1]}x: ${cred[String(parcelas[0])]}%–${cred[String(parcelas[parcelas.length - 1])]}%`}</>}
                    {o.tipo === 'link' && <> · Pix: <strong>{o.taxa_pix}%</strong></>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEdit(o)} className="btn-secondary !py-1 !px-2 text-xs"><Pencil size={13} /></button>
                  <button onClick={() => pedirExclusao({ descricao: `a operadora "${o.nome}"`, onConfirm: () => excluir.mutate(o.id) })}
                    className="btn-secondary !py-1 !px-2 text-xs text-rose-700"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          )
        })}
        {!isLoading && ops.length === 0 && (
          <div className="text-sm text-ink-400 bg-ink-50 rounded-lg p-4">Nenhuma operadora cadastrada. Adicione uma (ex.: FixPay) com suas taxas.</div>
        )}
      </div>
      {(nova || edit) && (
        <OperadoraModal operadora={edit}
          onClose={() => { setNova(false); setEdit(null) }}
          onDone={() => { setNova(false); setEdit(null); qc.invalidateQueries({ queryKey: ['operadoras-cartao'] }) }} />
      )}
    </div>
  )
}

function OperadoraModal({ operadora, onClose, onDone }: { operadora: Operadora | null; onClose: () => void; onDone: () => void }) {
  const [nome, setNome] = useState(operadora?.nome || '')
  const [tipo, setTipo] = useState<string>(operadora?.tipo || 'cartao')
  const [ativo, setAtivo] = useState(operadora?.ativo ?? true)
  const [taxaDebito, setTaxaDebito] = useState(String(operadora?.taxa_debito ?? ''))
  const [taxaPix, setTaxaPix] = useState(String(operadora?.taxa_pix ?? ''))
  // Crédito: 1 = à vista, 2..12 = parcelas sem juros. Guardamos como texto por chave.
  const [cred, setCred] = useState<Record<string, string>>(() => {
    const src = operadora?.taxas_credito || {}
    const o: Record<string, string> = {}
    for (let n = 1; n <= 18; n++) o[String(n)] = src[String(n)] != null ? String(src[String(n)]) : ''
    return o
  })
  const setCredN = (n: number, v: string) => setCred(c => ({ ...c, [String(n)]: v }))

  const salvar = useMutation({
    mutationFn: async () => {
      const taxas_credito: Record<string, number> = {}
      for (let n = 1; n <= 18; n++) {
        const t = parseFloat((cred[String(n)] || '').replace(',', '.'))
        if (!Number.isNaN(t)) taxas_credito[String(n)] = t
      }
      const body = {
        nome: nome.trim(), tipo, ativo, ordem: operadora?.ordem ?? 0,
        taxa_debito: parseFloat((taxaDebito || '0').replace(',', '.')) || 0,
        taxas_credito: Object.keys(taxas_credito).length ? taxas_credito : null,
        taxa_pix: parseFloat((taxaPix || '0').replace(',', '.')) || 0,
      }
      return operadora
        ? (await api.put(`/caixa/operadoras-cartao/${operadora.id}`, body)).data
        : (await api.post('/caixa/operadoras-cartao', body)).data
    },
    onSuccess: () => { toast.success('Operadora salva'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{operadora ? 'Editar operadora' : 'Nova operadora'}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div>
            <div className="text-xs font-medium mb-1">Tipo</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setTipo('cartao')}
                className={`flex-1 py-1.5 rounded-lg text-sm font-medium border ${tipo === 'cartao' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
                Cartão (máquina)
              </button>
              <button type="button" onClick={() => setTipo('link')}
                className={`flex-1 py-1.5 rounded-lg text-sm font-medium border ${tipo === 'link' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
                Link de pagamento
              </button>
            </div>
            <p className="text-[11px] text-ink-400 mt-1">
              {tipo === 'link'
                ? 'Vira a forma "Link" no PDV — o operador escolhe Débito, Crédito ou Pix na hora.'
                : 'Aparece no PDV sob as formas Débito e Crédito.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-2 sm:col-span-1"><div className="text-xs font-medium mb-1">Nome *</div>
              <input className="input w-full" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="FixPay" /></label>
            <label className="block col-span-2 sm:col-span-1"><div className="text-xs font-medium mb-1">Taxa Débito %</div>
              <input type="number" step="0.01" min="0" className="input w-full" value={taxaDebito} onChange={(e) => setTaxaDebito(e.target.value)} placeholder="1.19" /></label>
          </div>

          {tipo === 'link' && (
            <label className="block">
              <div className="text-xs font-medium mb-1">Taxa Pix %</div>
              <input type="number" step="0.01" min="0" className="input w-full" value={taxaPix} onChange={(e) => setTaxaPix(e.target.value)} placeholder="0.00" />
            </label>
          )}

          <div className="rounded-lg border border-ink-200 p-3 space-y-2">
            <div className="text-xs font-semibold text-ink-700">Taxas de crédito (%) <span className="font-normal text-ink-400">— sem juros</span></div>
            <div className="grid grid-cols-3 gap-2">
              <label className="block">
                <div className="text-[11px] text-ink-500 mb-0.5">À vista</div>
                <input type="number" step="0.01" min="0" className="input w-full !py-1 text-sm" value={cred['1']} onChange={(e) => setCredN(1, e.target.value)} placeholder="4.45" />
              </label>
              {Array.from({ length: 17 }, (_, i) => i + 2).map(n => (
                <label className="block" key={n}>
                  <div className="text-[11px] text-ink-500 mb-0.5">{n}x</div>
                  <input type="number" step="0.01" min="0" className="input w-full !py-1 text-sm" value={cred[String(n)]} onChange={(e) => setCredN(n, e.target.value)} placeholder="—" />
                </label>
              ))}
            </div>
            <p className="text-[11px] text-ink-400">Deixe em branco a parcela que a operadora não oferece.</p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
            <span className="text-sm">Ativa (aparece no PDV)</span>
          </label>
        </div>
        <div className="border-t border-ink-200 p-3 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!nome.trim() || salvar.isPending} onClick={() => salvar.mutate()}>
            {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
          </button>
        </div>
      </div>
    </div>
  )
}

function Prazos({ forma }: { forma: string }) {
  const qc = useQueryClient()
  const { data: conds = [] } = useQuery<Condicao[]>({
    queryKey: ['condicoes', forma],
    queryFn: async () => (await api.get('/caixa/condicoes-pagamento', { params: { forma } })).data,
  })
  const [nome, setNome] = useState('')
  const [dias, setDias] = useState('')  // "30/60"

  const criar = useMutation({
    mutationFn: async () => {
      const arr = dias.split(/[\/,\s]+/).map((x) => parseInt(x.trim())).filter((n) => !isNaN(n))
      return (await api.post('/caixa/condicoes-pagamento', { forma, nome: nome || dias, dias: arr })).data
    },
    onSuccess: () => { setNome(''); setDias(''); toast.success('Prazo criado'); qc.invalidateQueries({ queryKey: ['condicoes', forma] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/caixa/condicoes-pagamento/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['condicoes', forma] }) },
  })

  return (
    <div className="mt-3 pt-3 border-t border-ink-100">
      <div className="text-xs font-semibold text-ink-500 uppercase flex items-center gap-1 mb-2"><CalendarClock size={13} /> Prazos / condições</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {conds.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1 text-xs bg-ink-100 rounded px-2 py-1">
            <strong>{c.nome}</strong> <span className="text-ink-400">({c.parcelas}x: {c.dias.join('/')}d)</span>
            <button onClick={() => pedirExclusao({ descricao: `o prazo "${c.nome}"`, onConfirm: () => excluir.mutate(c.id) })} className="text-rose-600 ml-1"><X size={11} /></button>
          </span>
        ))}
        {conds.length === 0 && <span className="text-xs text-ink-400">Nenhum prazo. Ex: "30/60" = 2 parcelas (30 e 60 dias).</span>}
      </div>
      <div className="flex gap-2 items-center">
        <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome (ex: 30/60)" className="input !py-1 text-sm w-32" />
        <input value={dias} onChange={(e) => setDias(e.target.value)} placeholder="Dias (ex: 30/60)" className="input !py-1 text-sm w-32" />
        <button onClick={() => dias && criar.mutate()} disabled={criar.isPending} className="btn-secondary !py-1 !px-2 text-xs">
          {criar.isPending ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} add prazo
        </button>
      </div>
    </div>
  )
}

function FormaModal({ forma, contas, onClose, onDone }: { forma: Forma | null; contas: any[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    forma: forma?.forma || '', label: forma?.label || '',
    conta_id: forma?.conta_id || '', ativo: forma?.ativo ?? true,
    taxa_pct: String(forma?.taxa_pct ?? 0), dias_recebimento: String(forma?.dias_recebimento ?? 0),
    a_prazo: forma?.a_prazo ?? false,
    tpag: forma?.tpag || '',
    // BUG (13/10): este campo NÃO era enviado no PUT, e o ConfigFormaIn tem
    // default True — então editar "Recebimento de bike" ou "Crédito do cliente"
    // (que são gera_receber=False) nesta tela ligava a geração de conta a receber
    // sem ninguém pedir. Agora vai junto e preserva o valor.
    gera_receber: forma?.gera_receber ?? true,
  })
  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }))
  // FEATURE 30/06: taxa por nº de parcelas (ex.: cartão crédito). Editável como lista
  // {parcelas, taxa}; convertida p/ dict {"N": taxa} ao salvar.
  const [taxasParc, setTaxasParc] = useState<{ p: string; t: string }[]>(() =>
    Object.entries(forma?.taxas_parcelas || {})
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([p, t]) => ({ p: String(p), t: String(t) }))
  )
  const addTaxa = () => setTaxasParc(s => [...s, { p: '', t: '' }])
  const setTaxa = (i: number, k: 'p' | 't', v: string) => setTaxasParc(s => s.map((r, j) => j === i ? { ...r, [k]: v } : r))
  const delTaxa = (i: number) => setTaxasParc(s => s.filter((_, j) => j !== i))
  const salvar = useMutation({
    mutationFn: async () => {
      const taxas_parcelas: Record<string, number> = {}
      for (const r of taxasParc) {
        const p = parseInt(r.p); const t = parseFloat(r.t)
        if (p > 0 && !Number.isNaN(t)) taxas_parcelas[String(p)] = t
      }
      const body = {
        forma: f.forma, label: f.label, conta_id: f.conta_id ? Number(f.conta_id) : null,
        ativo: f.ativo, taxa_pct: parseFloat(f.taxa_pct) || 0,
        taxas_parcelas: Object.keys(taxas_parcelas).length ? taxas_parcelas : null,
        dias_recebimento: parseInt(f.dias_recebimento) || 0, a_prazo: f.a_prazo,
        gera_receber: f.gera_receber,
        tpag: f.tpag || null,
      }
      return forma
        ? (await api.put(`/caixa/config-formas/${forma.id}`, body)).data
        : (await api.post('/caixa/config-formas', body)).data
    },
    onSuccess: () => { toast.success('Forma salva'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{forma ? 'Editar forma' : 'Nova forma'}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><div className="text-xs font-medium mb-1">Código *</div>
              <input className="input w-full" value={f.forma} disabled={!!forma} onChange={(e) => set('forma', e.target.value)} placeholder="boleto" /></label>
            <label className="block"><div className="text-xs font-medium mb-1">Nome *</div>
              <input className="input w-full" value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="Boleto" /></label>
          </div>
          <label className="block">
            <div className="text-xs font-medium mb-1">Meio de pagamento na nota fiscal</div>
            <select className="input w-full" value={f.tpag} onChange={(e) => set('tpag', e.target.value)}>
              <option value="">Automático (pelo código da forma)</option>
              {TPAG_OPCOES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <p className="text-[11px] text-ink-400 mt-0.5">
              É o código que sai no XML da NF-e. Em "Automático", formas que o sistema
              não reconhece entram como <strong>99 — Outros</strong>.
            </p>
          </label>

          <label className="block"><div className="text-xs font-medium mb-1">Conta que recebe</div>
            <select className="input w-full" value={f.conta_id} onChange={(e) => set('conta_id', e.target.value)}>
              <option value="">—</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><div className="text-xs font-medium mb-1">Taxa % {taxasParc.length > 0 ? '(padrão)' : ''}</div>
              <input type="number" step="0.01" className="input w-full" value={f.taxa_pct} onChange={(e) => set('taxa_pct', e.target.value)} /></label>
            <label className="block"><div className="text-xs font-medium mb-1">Dias p/ receber</div>
              <input type="number" className="input w-full" value={f.dias_recebimento} onChange={(e) => set('dias_recebimento', e.target.value)} /></label>
          </div>

          {/* FEATURE 30/06: taxa por nº de parcelas (ex.: cartão de crédito 1x, 2x...). */}
          <div className="rounded-lg border border-ink-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-ink-700">Taxas por parcela <span className="font-normal text-ink-400">(opcional — ex.: cartão crédito)</span></div>
              <button type="button" onClick={addTaxa} className="text-xs text-brand-700 hover:text-brand-800 flex items-center gap-1">
                <Plus size={13} /> Adicionar
              </button>
            </div>
            {taxasParc.length === 0 ? (
              <p className="text-[11px] text-ink-400">Sem taxa por parcela. Usa a "Taxa %" padrão acima em todas as parcelas.</p>
            ) : (
              <p className="text-[11px] text-ink-500 bg-ink-50 rounded px-2 py-1">
                A taxa da parcela <strong>substitui</strong> a padrão (não soma). Parcelas sem taxa aqui usam a <strong>{f.taxa_pct || 0}%</strong> padrão.
              </p>
            )}
            {taxasParc.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-ink-400 px-0.5">
                  <span className="w-20">Parcelas</span><span className="w-24">Taxa %</span>
                </div>
                {taxasParc.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="number" min="1" step="1" placeholder="ex: 6" value={r.p}
                      onChange={e => setTaxa(i, 'p', e.target.value)}
                      className="input w-20 text-sm py-1" />
                    <span className="text-ink-400 text-xs">x →</span>
                    <input type="number" min="0" step="0.01" placeholder="%" value={r.t}
                      onChange={e => setTaxa(i, 't', e.target.value)}
                      className="input w-24 text-sm py-1" />
                    <button type="button" onClick={() => delTaxa(i)} className="p-1 text-ink-400 hover:text-rose-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.a_prazo} onChange={(e) => set('a_prazo', e.target.checked)} />
            <span className="text-sm">É <strong>a prazo</strong> (tem prazos/parcelas, ex: boleto)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.ativo} onChange={(e) => set('ativo', e.target.checked)} />
            <span className="text-sm">Ativo</span>
          </label>
        </div>
        <div className="border-t border-ink-200 p-3 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!f.forma || !f.label || salvar.isPending} onClick={() => salvar.mutate()}>
            {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
          </button>
        </div>
      </div>
    </div>
  )
}
